/**
 * Connection point markers - Shows spheres at available connection points
 * when a module is selected in the scene.
 */

import { useState, useCallback, useRef } from "react";
import * as THREE from "three";
import { ThreeEvent } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import {
  useSelectedModuleInstanceId,
  useSelectedModuleRootLink,
  useSelectedModuleCanBeChild,
  useAvailableConnections,
  useInstances,
} from "../store";
import { useWebSocket } from "../WebSocket";
import { useConnectionPointLogic } from "./useConnectionPointLogic";

// Marker configuration
const MARKER_RADIUS = 0.015;
const MARKER_SEGMENTS = 16;

// Colors for available connection points
const COLORS = {
  available: "#ffc107", // amber/yellow
  availableHover: "#ffea00",
};

interface MarkerProps {
  position: [number, number, number];
  color: string;
  hoverColor: string;
  label: string;
  onClick?: () => void;
  onHover?: (hovered: boolean) => void;
}

function Marker({ position, color, hoverColor, label, onClick, onHover }: MarkerProps) {
  const [hovered, setHovered] = useState(false);
  const meshRef = useRef<THREE.Mesh>(null);

  const handlePointerEnter = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      setHovered(true);
      document.body.style.cursor = "pointer";
      onHover?.(true);
    },
    [onHover]
  );

  const handlePointerLeave = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation();
      setHovered(false);
      document.body.style.cursor = "default";
      onHover?.(false);
    },
    [onHover]
  );

  // Use onPointerDown instead of onClick to avoid timing issues
  // When hover triggers preview transforms, React re-renders can cause
  // the mesh to unmount/remount, which breaks onClick event registration.
  // onPointerDown fires immediately before any state updates can interfere.
  const handlePointerDown = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      console.log("[Marker] onPointerDown fired");
      e.stopPropagation();
      onClick?.();
    },
    [onClick]
  );

  const scale = hovered ? 1.3 : 1.0;
  const currentColor = hovered ? hoverColor : color;

  return (
    <group position={position}>
      {/* Invisible larger sphere for more reliable click detection */}
      <mesh
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onPointerDown={handlePointerDown}
      >
        <sphereGeometry args={[MARKER_RADIUS * 2.5, 8, 8]} />
        <meshBasicMaterial visible={false} />
      </mesh>
      {/* Visible marker sphere */}
      <mesh
        ref={meshRef}
        scale={[scale, scale, scale]}
      >
        <sphereGeometry args={[MARKER_RADIUS, MARKER_SEGMENTS, MARKER_SEGMENTS]} />
        <meshStandardMaterial
          color={currentColor}
          emissive={currentColor}
          emissiveIntensity={hovered ? 0.5 : 0.2}
          transparent
          opacity={0.9}
        />
      </mesh>
      {/* Tooltip on hover */}
      {hovered && (
        <Html
          position={[0, MARKER_RADIUS * 2, 0]}
          center
          style={{
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          <div
            style={{
              background: "rgba(0, 0, 0, 0.8)",
              color: "white",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              fontFamily: "monospace",
            }}
          >
            {label}
          </div>
        </Html>
      )}
    </group>
  );
}

export function ConnectionPointMarkers() {
  const selectedInstanceId = useSelectedModuleInstanceId();
  const rootLink = useSelectedModuleRootLink();
  const canBeChild = useSelectedModuleCanBeChild();
  const availableConnections = useAvailableConnections();
  const instances = useInstances();
  const { sendConnectModules, sendPreviewConnection, sendSelectInstance } = useWebSocket();

  const {
    shouldRender,
    validConnections,
    handleClick,
    handleHover,
  } = useConnectionPointLogic({
    selectedInstanceId,
    rootLink,
    availableConnections,
    instances,
    canBeChild,
    actions: {
      sendConnectModules,
      sendPreviewConnection,
      sendSelectInstance,
    },
  });

  if (!shouldRender) {
    return null;
  }

  return (
    <group>
      {/* Available connection points from other modules (clickable) */}
      {validConnections.map((conn) => (
        <Marker
          key={`available-${conn.instance_id}-${conn.link_name}`}
          position={conn.position}
          color={COLORS.available}
          hoverColor={COLORS.availableHover}
          label={`${conn.module_name} / ${conn.link_name}`}
          onClick={() => handleClick(conn.instance_id, conn.link_name)}
          onHover={(hovered) => handleHover(conn.instance_id, conn.link_name, hovered)}
        />
      ))}
    </group>
  );
}
